/* eslint-disable react-hooks/rules-of-hooks */
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSnackbar } from "components/GlobalSnackbar";
import { handleApiError } from "utils/errorHandler";

// @mui material components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";

// API
import { apiGetAllHarta, apiSearchHarta } from "api/harta";

export default function dataHarta({
  onRowSelect,
  selectedRow,
  currentPage,
  itemsPerPage,
  search,
  refreshTrigger,
  visibleColumns = {
    kodharta: true,
    alamat: true,
    zonharta: true,
    kategorikutipan: true,
    kadarsewa: true,
  },
  editingCell = null,
  editValue = '',
  onCellEdit = () => {},
  onSaveEdit = () => {},
  onCancelEdit = () => {},
  onEditValueChange = () => {},
  onEditKeyDown = () => {},
  saving = false,
  zonOptions = [],
  kategoriOptions = [],
}) {
  const [hartaData, setHartaData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const { showError } = useSnackbar();

  useEffect(() => {
    const fetchHarta = async () => {
      try {
        setLoading(true);
        // Check if search parameters are provided
        const hasSearchParams =
          search && Object.values(search).some((value) => value && value.trim() !== "");
        let response;
        if (hasSearchParams) {
          const searchParams = {};
          if (search.kod_harta && search.kod_harta.trim())
            searchParams.kod_harta = search.kod_harta.trim();
          if (search.zon && search.zon.trim()) searchParams.zon = search.zon.trim();
          if (search.kategori_kutipan && search.kategori_kutipan.trim())
            searchParams.kategori_kutipan = search.kategori_kutipan.trim();
          response = await apiSearchHarta(searchParams, currentPage, itemsPerPage);
        } else {
          response = await apiGetAllHarta(currentPage, itemsPerPage);
        }
        if (response && response.data) {
          setHartaData(response.data);
          setTotalPages(response.pagination.totalPages);
          setTotalItems(response.pagination.totalItems);
        } else if (Array.isArray(response)) {
          setHartaData(response);
          setTotalPages(1);
          setTotalItems(response.length);
        } else {
          setHartaData([]);
          setTotalPages(1);
          setTotalItems(0);
        }
      } catch (error) {
        handleApiError(error, showError);
        setHartaData([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };
    fetchHarta();
  }, [search, currentPage, itemsPerPage, refreshTrigger]);

  const KodHarta = ({ kod }) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDTypography variant="button" fontWeight="medium" ml={0.05} lineHeight={1}>
        {kod}
      </MDTypography>
    </MDBox>
  );

  // Prop types for internal component to satisfy ESLint
  KodHarta.propTypes = {
    kod: PropTypes.string,
  };

  KodHarta.defaultProps = {
    kod: "",
  };

  const generateRows = () => {
    if (loading) {
      return [
        {
          select: (
            <input type="checkbox" checked={false} disabled style={{ cursor: "not-allowed" }} />
          ),
          kodharta: <KodHarta kod="Loading..." />,
          alamat: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              Loading...
            </MDTypography>
          ),
          zonharta: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              Loading...
            </MDTypography>
          ),
          kategorikutipan: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              Loading...
            </MDTypography>
          ),
          kadarsewa: (
            <MDTypography variant="caption" color="text" fontWeight="medium">
              Loading...
            </MDTypography>
          ),
        },
      ];
    }

    return hartaData.map((harta, idx) => {
      // Ensure every row has a unique, defined id
      const rowId = harta.id || harta.kod_harta || harta.kodharta || idx;
      // Combine alamat1_harta and alamat2_harta for display
      const alamatParts = [
        harta.alamat1_harta,
        harta.alamat2_harta,
        harta.poskod_harta,
        harta.bandar_harta,
        harta.negeri_harta,
      ].filter(Boolean);
      const alamatCombined = alamatParts.join(", ");

      return {
        id: rowId,
        select: (
          <input
            type="checkbox"
            checked={!!(selectedRow && selectedRow.id === rowId)}
            onChange={() => onRowSelect && onRowSelect({ ...harta, id: rowId })}
            style={{ cursor: "pointer" }}
          />
        ),
        kodharta: <KodHarta kod={harta.kod_harta || "Unknown"} />,
        alamat: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {alamatCombined || "-"}
          </MDTypography>
        ),
        zonharta: editingCell?.rowId === rowId && editingCell?.column === 'zon' ? (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <Select
              value={editValue}
              onChange={(e) => onEditValueChange(e.target.value)}
              onKeyDown={onEditKeyDown}
              size="small"
              autoFocus
              disabled={saving}
              sx={{
                minWidth: 120,
                fontSize: '0.875rem',
                '& .MuiSelect-select': { py: 0.5 }
              }}
            >
              {zonOptions.map((zon) => (
                <MenuItem key={zon} value={zon}>{zon}</MenuItem>
              ))}
            </Select>
            <IconButton size="small" onClick={onSaveEdit} disabled={saving} color="success">
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onCancelEdit} disabled={saving} color="error">
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
        ) : (
          <MDBox
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: 1,
              },
              px: 1,
              py: 0.5,
            }}
            onClick={() => onCellEdit(rowId, 'zon', harta.zon, harta)}
          >
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {harta.zon || "-"}
            </MDTypography>
            <EditIcon sx={{ fontSize: 14, color: 'action.disabled', opacity: 0.5 }} />
          </MDBox>
        ),
        kategorikutipan: editingCell?.rowId === rowId && editingCell?.column === 'kategori_kutipan' ? (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <Select
              value={editValue}
              onChange={(e) => onEditValueChange(e.target.value)}
              onKeyDown={onEditKeyDown}
              size="small"
              autoFocus
              disabled={saving}
              sx={{
                minWidth: 150,
                fontSize: '0.875rem',
                '& .MuiSelect-select': { py: 0.5 }
              }}
            >
              {kategoriOptions.map((kat) => (
                <MenuItem key={kat} value={kat}>{kat}</MenuItem>
              ))}
            </Select>
            <IconButton size="small" onClick={onSaveEdit} disabled={saving} color="success">
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onCancelEdit} disabled={saving} color="error">
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
        ) : (
          <MDBox
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: 1,
              },
              px: 1,
              py: 0.5,
            }}
            onClick={() => onCellEdit(rowId, 'kategori_kutipan', harta.kategori_kutipan, harta)}
          >
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {harta.kategori_kutipan || "-"}
            </MDTypography>
            <EditIcon sx={{ fontSize: 14, color: 'action.disabled', opacity: 0.5 }} />
          </MDBox>
        ),
        kadarsewa: editingCell?.rowId === rowId && editingCell?.column === 'kadar_sewa' ? (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <TextField
              value={editValue}
              onChange={(e) => onEditValueChange(e.target.value)}
              onKeyDown={onEditKeyDown}
              type="number"
              size="small"
              autoFocus
              disabled={saving}
              inputProps={{ 
                min: 0, 
                step: 0.01,
                style: { textAlign: 'right' }
              }}
              sx={{
                width: 120,
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem',
                  py: 0.5,
                }
              }}
            />
            <IconButton size="small" onClick={onSaveEdit} disabled={saving} color="success">
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onCancelEdit} disabled={saving} color="error">
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
        ) : (
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            gap={1}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: 1,
              },
              px: 1,
              py: 0.5,
            }}
            onClick={() => onCellEdit(rowId, 'kadar_sewa', harta.kadar_sewa, harta)}
          >
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {harta.kadar_sewa ? `RM ${parseFloat(harta.kadar_sewa).toFixed(2)}` : "-"}
            </MDTypography>
            <EditIcon sx={{ fontSize: 14, color: 'action.disabled', opacity: 0.5 }} />
          </MDBox>
        ),
      };
    });
  };

  // Filter columns based on visibility
  const allColumns = [
    { Header: "", accessor: "select", width: "5%", align: "center", headerAlign: "center", alwaysVisible: true },
    { Header: "kod harta", accessor: "kodharta", width: "15%", align: "center", headerAlign: "center", visible: visibleColumns.kodharta },
    { Header: "alamat", accessor: "alamat", width: "35%", align: "left", headerAlign: "center", visible: visibleColumns.alamat },
    { Header: "zon", accessor: "zonharta", width: "15%", align: "center", headerAlign: "center", visible: visibleColumns.zonharta },
    { Header: "kategori kutipan", accessor: "kategorikutipan", width: "15%", align: "center", headerAlign: "center", visible: visibleColumns.kategorikutipan },
    { Header: "kadar sewa", accessor: "kadarsewa", width: "15%", align: "right", headerAlign: "center", visible: visibleColumns.kadarsewa },
  ];

  const visibleColumnsArray = allColumns.filter(col => col.alwaysVisible || col.visible !== false);

  return {
    columns: visibleColumnsArray,

    rows: generateRows(),
    pagination: {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage,
      actualItemsOnPage: hartaData.length, // Add actual items displayed on current page
      hasNextPage: currentPage < totalPages,
      hasPreviousPage: currentPage > 1,
    },
  };
}
